import { Customer, WithdrawalAccount } from '../../state/Radom'
import { getPaymentMethods } from '../../util/Managed'
import { shortAddress } from '../../util/Util'

/**
 * Destination-first model for the unified Send flow. Selecting a destination
 * derives the booking rails: the merchant's own saved accounts settle as
 * withdrawals, everything else (customers and one-off addresses) settles as
 * payouts.
 */

export type OneOffKind = 'evm' | 'solana' | 'tron' | 'iban' | 'other'

export type SendDestination =
  | { kind: 'ownAccount', account: WithdrawalAccount }
  | { kind: 'customer', customer: Customer }
  | { kind: 'oneOff', value: string, oneOffKind: OneOffKind }

export type SendMode = 'withdrawal' | 'payout'

export const destinationMode = (destination: SendDestination): SendMode => {
  if (destination.kind === 'ownAccount') return 'withdrawal'
  // Addresses outside the payout engine's networks save as wallet accounts
  // and settle on the withdrawal rail.
  if (destination.kind === 'oneOff' && destination.oneOffKind === 'other') return 'withdrawal'
  return 'payout'
}

export const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/
// Real Solana pubkeys encode to 43-44 base58 characters; the loose lower bound
// the chain allows (32) collides with legacy UTXO addresses, so stay strict.
export const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{40,44}$/
export const TRON_ADDRESS_REGEX = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
export const IBAN_REGEX = /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{10,30}$/

/**
 * Address shapes for the withdrawal networks that aren't EVM, Solana or Tron.
 * A pasted address matching one of these becomes an 'other' one-off that
 * settles as a withdrawal to a saved wallet account (the only rail with full
 * network coverage). Legacy 1... addresses are valid on both Bitcoin forks,
 * so both networks stay candidates and the sender picks.
 */
const OTHER_NETWORK_ADDRESS_SHAPES: Array<{ network: string, regex: RegExp }> = [
  { network: 'Bitcoin', regex: /^(bc1[a-z0-9]{11,87}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/ },
  { network: 'BitcoinCash', regex: /^((bitcoincash:)?[qp][a-z0-9]{41}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/ },
  { network: 'Litecoin', regex: /^(ltc1[a-z0-9]{11,87}|[LM][1-9A-HJ-NP-Za-km-z]{26,33})$/ },
  { network: 'Dogecoin', regex: /^D[1-9A-HJ-NP-Za-km-z]{25,34}$/ },
  { network: 'Dash', regex: /^X[1-9A-HJ-NP-Za-km-z]{33}$/ },
  { network: 'Zcash', regex: /^t[13][1-9A-HJ-NP-Za-km-z]{33}$/ },
  { network: 'Cardano', regex: /^(addr1[a-z0-9]{50,110}|(Ae2|DdzFF)[1-9A-HJ-NP-Za-km-z]{40,110})$/ },
  { network: 'Polkadot', regex: /^1[1-9A-HJ-NP-Za-km-z]{44,50}$/ },
  { network: 'PolkadotAssetHub', regex: /^1[1-9A-HJ-NP-Za-km-z]{44,50}$/ }
]

/**
 * Networks whose address shape matches a pasted 'other' destination, limited
 * to networks the platform still offers (delisted chains are hidden or
 * disabled in the method registry and must not be offered as save targets).
 */
export const oneOffNetworkCandidates = (raw: string): string[] => {
  const value = normalizeOneOffInput(raw)
  const offeredNetworks = new Set(getPaymentMethods()
    .filter(method => !method.hostChain && !method.isDisabled && !method.isHidden && !method.isTestnet)
    .map(method => method.name))
  return OTHER_NETWORK_ADDRESS_SHAPES
    .filter(shape => offeredNetworks.has(shape.network))
    .filter(shape => shape.regex.test(value))
    .map(shape => shape.network)
}

export const normalizeOneOffInput = (raw: string): string => raw.trim().replace(/\s+/g, '')

/**
 * Detect whether free text pasted into the destination search is a sendable
 * one-off destination. Order matters: IBAN before Solana (country code + check
 * digits is the more specific shape), and the non-EVM chain shapes before
 * Solana so short legacy UTXO addresses aren't swallowed by base58 overlap.
 */
export const detectOneOffKind = (raw: string): OneOffKind | undefined => {
  const value = normalizeOneOffInput(raw)
  if (EVM_ADDRESS_REGEX.test(value)) return 'evm'
  if (IBAN_REGEX.test(value)) return 'iban'
  if (TRON_ADDRESS_REGEX.test(value)) return 'tron'
  if (oneOffNetworkCandidates(value).length > 0) return 'other'
  if (SOLANA_ADDRESS_REGEX.test(value)) return 'solana'
  return undefined
}

export const oneOffKindLabel = (kind: OneOffKind): string => {
  switch (kind) {
  case 'evm':
    return 'EVM address'
  case 'solana':
    return 'Solana address'
  case 'tron':
    return 'Tron address'
  case 'iban':
    return 'IBAN'
  case 'other':
    return 'Crypto address'
  }
}

/** Short human summary shown under a saved withdrawal account. */
export const withdrawalAccountSubtitle = (account: WithdrawalAccount): string => {
  const crypto = account.accountDetails.Crypto
  if (crypto) {
    return `${crypto.network} · ${shortAddress(crypto.address, 6)}`
  }

  const fiat = account.accountDetails.Fiat
  if (fiat?.accountType.iban) {
    return `SEPA · ${fiat.accountType.iban.iban}`
  }
  if (fiat?.accountType.us) {
    return `USD wire · ${fiat.accountType.us.accountNumber}`
  }

  return 'Bank account'
}

export const customerDisplayName = (customer: Customer): string =>
  customer.name?.trim() || customer.companyName?.trim() || customer.email?.trim() || shortAddress(customer.id, 6)

/**
 * Stable hue (0-359) derived from a recipient's display name so the same
 * person always gets the same avatar color, without storing anything.
 */
export const nameHue = (seed: string): number => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % 360
}

/** Up-to-two-letter monogram for avatar circles ("Acme GmbH" → "AG"). */
export const avatarInitials = (name: string): string => {
  const cleaned = name.trim()
  if (!cleaned) return '·'
  if (/^0x/i.test(cleaned)) return cleaned.slice(2, 4).toUpperCase()
  // Words are stripped of leading punctuation so "Company (SEPA)" → "CS".
  const words = cleaned
    .split(/\s+/)
    .map(word => word.replace(/^[^0-9a-zA-ZÀ-ɏ]+/, ''))
    .filter(Boolean)
  if (words.length === 0) return cleaned.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Identity key for deduping destinations (recent chips, lists). */
export const destinationKey = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return `account:${destination.account.id}`
  case 'customer':
    return `customer:${destination.customer.id}`
  case 'oneOff':
    return `oneOff:${destination.value}`
  }
}

/** Human name for a selected destination, used in review and success copy. */
export const destinationDisplayName = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return destination.account.name?.trim() || 'Withdrawal account'
  case 'customer':
    return customerDisplayName(destination.customer)
  case 'oneOff':
    return destination.oneOffKind === 'iban'
      ? destination.value
      : shortAddress(destination.value, 8)
  }
}

const EVM_NETWORK_NAMES = [
  'Ethereum',
  'SepoliaTestnet',
  'Base',
  'Arbitrum',
  'Polygon',
  'PolygonTestnet',
  'BNB',
  'BNBTestnet',
  'Avalanche'
]

/**
 * Whether a network can receive the given one-off address family. EVM chains
 * are recognised by chain id (with a name fallback for testnets that predate
 * chain-id metadata).
 */
export const networkAcceptsOneOffKind = (
  kind: OneOffKind,
  network: string,
  chainId?: number
): boolean => {
  switch (kind) {
  case 'evm':
    return !!chainId || EVM_NETWORK_NAMES.includes(network)
  case 'solana':
    return network === 'Solana' || network === 'SolanaDevnet' || network === 'SolanaTestnet'
  case 'tron':
    return network.startsWith('Tron')
  case 'iban':
  case 'other':
    // These settle through saved recipients/accounts, not one-off payouts.
    return false
  }
}

/** Address shape validation for batch rows against the chosen batch asset. */
export const addressMatchesNetwork = (
  address: string,
  network: string,
  chainId?: number
): boolean => {
  if (network.startsWith('Tron')) {
    return TRON_ADDRESS_REGEX.test(address)
  }
  if (network.startsWith('Solana')) {
    return SOLANA_ADDRESS_REGEX.test(address)
  }
  if (!!chainId || EVM_NETWORK_NAMES.includes(network)) {
    return EVM_ADDRESS_REGEX.test(address)
  }
  return false
}
